import { get, post, patch, put, del } from '@core/api/client';
import { endpoints } from '@core/api/endpoints';

export type RoleScope = 'platform' | 'tenant';
export type AgentGrantPreset = 'full' | 'view' | 'custom';

export interface PermissionCatalogItem {
  code: string;
  scope: RoleScope;
  resource: string;
  action: string;
  description: string;
  requiresAgentGrant: boolean;
}

export interface RoleAgentGrant {
  agentId: string;
  preset: AgentGrantPreset;
  permissions: string[];
}

export interface RoleDetail {
  id: string;
  scope: RoleScope;
  tenantId: string | null;
  name: string;
  description: string | null;
  isSystem: boolean;
  agentsUnrestricted: boolean;
  permissionCodes: string[];
  agentGrants: RoleAgentGrant[];
  assignmentCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRolePayload {
  name: string;
  description?: string | null;
  permissionCodes?: string[];
}

export interface UpdateRolePayload {
  name?: string;
  description?: string | null;
}

export const roleService = {
  listCatalog(scope: RoleScope): Promise<PermissionCatalogItem[]> {
    return get(endpoints.permissions.catalog, { params: { scope } });
  },

  listPlatformRoles(): Promise<RoleDetail[]> {
    return get(endpoints.platformRoles.list);
  },

  createPlatformRole(payload: CreateRolePayload): Promise<RoleDetail> {
    return post(endpoints.platformRoles.create, payload);
  },

  updatePlatformRole(
    roleId: string,
    payload: UpdateRolePayload,
  ): Promise<RoleDetail> {
    return patch(endpoints.platformRoles.update(roleId), payload);
  },

  deletePlatformRole(roleId: string): Promise<void> {
    return del(endpoints.platformRoles.delete(roleId));
  },

  setPlatformPermissions(
    roleId: string,
    permissionCodes: string[],
  ): Promise<RoleDetail> {
    return put(endpoints.platformRoles.permissions(roleId), {
      permissionCodes,
    });
  },

  listTenantRoles(): Promise<RoleDetail[]> {
    return get(endpoints.tenantRoles.list);
  },

  createTenantRole(payload: CreateRolePayload): Promise<RoleDetail> {
    return post(endpoints.tenantRoles.create, payload);
  },

  updateTenantRole(
    roleId: string,
    payload: UpdateRolePayload,
  ): Promise<RoleDetail> {
    return patch(endpoints.tenantRoles.update(roleId), payload);
  },

  deleteTenantRole(roleId: string): Promise<void> {
    return del(endpoints.tenantRoles.delete(roleId));
  },

  setTenantPermissions(
    roleId: string,
    permissionCodes: string[],
  ): Promise<RoleDetail> {
    return put(endpoints.tenantRoles.permissions(roleId), {
      permissionCodes,
    });
  },

  listAgentGrants(roleId: string): Promise<RoleAgentGrant[]> {
    return get(endpoints.tenantRoles.agentGrants(roleId));
  },

  setAgentGrants(
    roleId: string,
    grants: RoleAgentGrant[],
  ): Promise<RoleAgentGrant[]> {
    return put(endpoints.tenantRoles.agentGrants(roleId), { grants });
  },
} as const;
